import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	ProjectSaleableUnit,
	useCreateSaleableUnitMutation,
	useUpdateSaleableUnitMutation,
} from "@repo/server-api";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import { validErrorState } from "@repo/legacy/components/helper/Index";

export default function DialogServiceUnit(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		unit: ProjectSaleableUnit | null;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [unit, setUnit] = useState<ProjectSaleableUnit | null>(null);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);
	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [createUnit] = useCreateSaleableUnitMutation({
		variables: {
			name: unit?.name ?? "",
			shortCode: unit?.shortCode ?? "",
		},
		onCompleted(data) {
			if (data.createSaleableUnit.saleableUnit) {
				props.refreshData();
				handleClose();
			}
		},
	});
	const [updateUnit] = useUpdateSaleableUnitMutation({
		variables: {
			id: unit?.id ?? "",
			name: unit?.name ?? "",
			shortCode: unit?.shortCode ?? "",
		},
		onCompleted(data) {
			if (data) {
				props.refreshData();
				handleClose();
			}
		},
	});

	useEffect(() => {
		setOpen(props.open);
	}, [props.open]);

	useEffect(() => {
		if (props.unit) {
			setUnit({
				...props.unit,
			});
		}
	}, [props.unit]);

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(t("validation.name.required")),
		shortCode: Yup.string().required(t("validation.shortCode.required")),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(unit, { abortEarly: false });
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleClose = () => {
		setOpen(false);
		setErrors({});
		setUnit(props.unit);
		props.setOpen(false);
		setSaveClicked(false);
	};

	const handleSubmit = async () => {
		const isValid = await validateFields();
		if (!isValid) {
			return;
		}
		setSaveClicked(true);
		if (unit?.id) {
			updateUnit();
			return;
		}
		createUnit();
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth={true}>
			<DialogTitle>{t("unit") + " " + (props.unit?.name ?? "")}</DialogTitle>
			<DialogContent className="flex-col gap-4">
				<Box className="fullCenter">
					<TextField
						onChange={(event) => {
							setUnit(unit ? { ...unit, name: event.target.value ?? "" } : null);
							setErrors({ ...errors, name: "" });
						}}
						error={errors.name ? true : false}
						helperText={errors.name as string}
						className="w-100"
						value={unit?.name ?? ""}
						label={t("name")}
						variant="outlined"
					/>
				</Box>
				<Box className="fullCenter">
					<TextField
						onChange={(event) => {
							setUnit(unit ? { ...unit, shortCode: event.target.value ?? "" } : null);
							setErrors({ ...errors, shortCode: "" });
						}}
						error={errors.shortCode ? true : false}
						helperText={errors.shortCode as string}
						className="w-100"
						value={unit?.shortCode ?? ""}
						label={t("short-code")}
						variant="outlined"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					data-testid="dialog-submit-team-member"
					onClick={handleSubmit}
					variant="contained"
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
